import {
	DEMAND_LEVEL,
	OCR_DOC_COUNTRY_TYPE,
} from '../models/workflows.models';
import { IOcrOperation } from '../../operations/models/operations.models';

/**
 * Utilidades OCR del editor de workflows (NIF-530).
 *
 * Abrir cuando: demand level / países DNI-pasaporte / checks antifraude / setting RECOMMENDED / fallback manual.
 * Sin dependencias Angular — puro y testeable.
 */

/** Claves de los checks anidados en `fraud_scoring`. */
export type OcrFraudCheckKey =
	| 'manipulated_photo'
	| 'photocopy_check'
	| 'screen_photo'
	| 'identifier_blocked';

export const OCR_FRAUD_CHECK_KEYS: readonly OcrFraudCheckKey[] = [
	'manipulated_photo',
	'photocopy_check',
	'screen_photo',
	'identifier_blocked',
] as const;

/** Campos ofuscables (NIF-536). El form guarda un multiselect de estas claves. */
export const OCR_OBFUSCATION_FIELD_KEYS = [
	'photo',
	'identifier',
	'names',
	'surnames',
	'gender',
	'nationality',
	'birthdate',
	'expedition_date',
	'expiration_date',
	'support_number',
	'address',
	'place_of_birth',
	'team_number',
	'parents_names',
	'mrz',
] as const;

export type OcrObfuscationFieldKey = (typeof OCR_OBFUSCATION_FIELD_KEYS)[number];

export function ocrObfuscationFieldI18nKey(field: OcrObfuscationFieldKey): string {
	return `WORKFLOWS.FORM.OPERATIONS.OBFUSCATION_FIELD_${field.toUpperCase()}`;
}

/** Ids de `tec-select` multiple (`ISelect[]` o string[]). */
export function mapOcrMultiSelectIds(raw: unknown): string[] {
	if (!Array.isArray(raw)) {
		return [];
	}
	return raw.map((item: { id?: string } | string) =>
		typeof item === 'object' && item && 'id' in item && item.id ? item.id : String(item),
	);
}

export function ocrObfuscationFieldsFromSelected(
	selected: readonly string[],
): Record<OcrObfuscationFieldKey, boolean> {
	const set = new Set(selected);
	return Object.fromEntries(
		OCR_OBFUSCATION_FIELD_KEYS.map((key) => [key, set.has(key)]),
	) as Record<OcrObfuscationFieldKey, boolean>;
}

export function ocrObfuscationSelectedFromFields(
	fields?: Partial<Record<string, boolean>> | null,
): OcrObfuscationFieldKey[] {
	if (!fields) {
		return [];
	}
	return OCR_OBFUSCATION_FIELD_KEYS.filter((key) => Boolean(fields[key]));
}

/** Preset RECOMMENDED: intentos y tiempo por cara (NIF-829: 2 intentos). */
export const OCR_RECOMMENDED_TOTAL_TRIES = 2;
export const OCR_RECOMMENDED_SIDE_MAX_TIME = 150;

/**
 * Infiera nivel de fraude OCR: RECOMMENDED si todos los checks + tries/tiempo del preset; si no CUSTOM.
 *
 * @param ocrOperation - Operación OCR del detalle/API
 */
export function getOcrFraudScoringType(ocrOperation: IOcrOperation): DEMAND_LEVEL {
	const scoring = ocrOperation.config?.fraud_scoring;
	const setting = ocrOperation.config?.setting;
	const allChecksOn = OCR_FRAUD_CHECK_KEYS.every((key) => Boolean(scoring?.[key]));
	const matchesRecommendedSetting =
		setting?.total_tries === OCR_RECOMMENDED_TOTAL_TRIES &&
		setting?.side_max_time === OCR_RECOMMENDED_SIDE_MAX_TIME;

	return allChecksOn && matchesRecommendedSetting ? DEMAND_LEVEL.RECOMMENDED : DEMAND_LEVEL.CUSTOM;
}

/**
 * Tipo de lista de países para DNI o pasaporte en el form OCR.
 *
 * - `allowed_docs[type].length === 0` → ALL (lista vacía explícita = todos)
 * - hay `allowed_docs` → INCLUDE
 * - hay `not_allowed_docs` → EXCLUDE
 * - sin datos → ALL
 *
 * @param ocrOperation - Operación OCR del detalle/API
 * @param type - `dni` | `passport`
 */
export function getOcrDocCountryType(
	ocrOperation: IOcrOperation,
	type: 'dni' | 'passport',
): OCR_DOC_COUNTRY_TYPE {
	if (ocrOperation?.config?.allowed_docs?.[type]?.length === 0) {
		return OCR_DOC_COUNTRY_TYPE.ALL;
	}
	if (ocrOperation?.config?.allowed_docs?.[type]?.length) {
		return OCR_DOC_COUNTRY_TYPE.INCLUDE;
	}
	if (ocrOperation?.config?.not_allowed_docs?.[type]?.length) {
		return OCR_DOC_COUNTRY_TYPE.EXCLUDE;
	}
	return OCR_DOC_COUNTRY_TYPE.ALL;
}

/**
 * Mapea un check de fraude del API al shape del FormGroup (available + flags + email).
 *
 * @param check - Bloque del API o `undefined`/`null` si el check no venía
 */
export function mapOcrFraudCheckFromApi(check: {
	blocking_step?: boolean;
	ip_block?: boolean;
	identifier_block?: boolean;
	email_alert?: string;
} | null | undefined): {
	available: boolean;
	blocking_step: boolean;
	ip_block: boolean;
	identifier_block: boolean;
	email_alert_available: boolean;
	email_alert: string;
} {
	return {
		available: Boolean(check),
		blocking_step: Boolean(check?.blocking_step),
		ip_block: Boolean(check?.ip_block),
		identifier_block: Boolean(check?.identifier_block),
		email_alert_available: Boolean(check?.email_alert),
		email_alert: check?.email_alert ?? '',
	};
}

/**
 * Setting `total_tries` / `side_max_time` al serializar:
 * nivel RECOMMENDED fuerza preset; si no, valores del form.
 *
 * @param isRecommendedDemand - `fraud_scoring.type === RECOMMENDED`
 * @param totalTriesFromForm - Valor crudo del form
 * @param sideMaxTimeFromForm - Valor crudo del form
 */
export function resolveOcrSettingTriesAndTime(
	isRecommendedDemand: boolean,
	totalTriesFromForm: unknown,
	sideMaxTimeFromForm: unknown,
): { total_tries: unknown; side_max_time: number } {
	return {
		total_tries: isRecommendedDemand ? OCR_RECOMMENDED_TOTAL_TRIES : totalTriesFromForm,
		side_max_time: isRecommendedDemand
			? OCR_RECOMMENDED_SIDE_MAX_TIME
			: Number(sideMaxTimeFromForm),
	};
}

/**
 * Fallback manual OCR solo si el form pide `manual` y la compañía no lo prohíbe.
 * Si hay compañía y `manual_validation === false` → `undefined` (no se envía).
 * Sin compañía cargada → se permite (mismo criterio legacy del payload).
 *
 * @param fallbackType - Tipo de fallback del form (`manual` | `auto` | …)
 * @param company - Compañía seleccionada o `undefined` si aún no hay
 * @param fallbackValidation - Validación del fallback
 * @param fallbackEmails - Emails si validation = manual
 * @returns Bloque API o `undefined` (no se envía)
 */
export function buildOcrManualFallbackOrUndefined(
	fallbackType: unknown,
	company: { manual_validation?: boolean } | null | undefined,
	fallbackValidation: unknown,
	fallbackEmails: unknown,
): { emails: unknown; type: unknown; validation: unknown } | undefined {
	if (fallbackType !== 'manual') {
		return undefined;
	}
	// Compañía sin validación manual: no persistir fallback manual.
	if (company && !company.manual_validation) {
		return undefined;
	}
	return {
		emails: fallbackValidation === 'manual' ? fallbackEmails : '',
		type: fallbackType,
		validation: fallbackValidation,
	};
}

/** Id plano de ISelect o valor crudo (mismo criterio legacy del form). */
function resolveOcrSelectOrPlain(value: unknown): unknown {
	if (value && typeof value === 'object' && 'id' in value) {
		return (value as { id: unknown }).id;
	}
	return value;
}

/** Países del form OCR → ids string (ISelect.id o plano). */
function mapOcrCountryIds(countries: unknown): string[] {
	return mapOcrMultiSelectIds(countries);
}

/**
 * `allowed_docs` para payload OCR según flags del form (ALL → `[]`, INCLUDE → lista, EXCLUDE → no enviar).
 *
 * Abrir cuando: serializas países DNI/pasaporte desde el editor.
 *
 * @param available - Switch `{doc}_available`
 * @param docTypeRaw - Control `{doc}_type` (ISelect o enum)
 * @param countriesRaw - Control `{doc}_countries`
 * @returns `[]` (todos), lista INCLUDE, o `undefined` (no aplica / EXCLUDE)
 */
export function resolveOcrAllowedDocsForPayload(
	available: boolean,
	docTypeRaw: unknown,
	countriesRaw: unknown,
): string[] | undefined {
	if (!available) {
		return undefined;
	}
	const docType = resolveOcrSelectOrPlain(docTypeRaw);
	if (docType === OCR_DOC_COUNTRY_TYPE.ALL) {
		return [];
	}
	if (docType === OCR_DOC_COUNTRY_TYPE.INCLUDE) {
		return mapOcrCountryIds(countriesRaw);
	}
	if (docType === OCR_DOC_COUNTRY_TYPE.EXCLUDE) {
		return undefined;
	}
	return undefined;
}

/**
 * `not_allowed_docs` para payload OCR (solo modo EXCLUDE).
 *
 * @param available - Switch `{doc}_available`
 * @param docTypeRaw - Control `{doc}_type`
 * @param countriesRaw - Control `{doc}_countries`
 */
export function resolveOcrNotAllowedDocsForPayload(
	available: boolean,
	docTypeRaw: unknown,
	countriesRaw: unknown,
): string[] | undefined {
	if (!available) {
		return undefined;
	}
	const docType = resolveOcrSelectOrPlain(docTypeRaw);
	if (docType === OCR_DOC_COUNTRY_TYPE.ALL || docType === OCR_DOC_COUNTRY_TYPE.INCLUDE) {
		return undefined;
	}
	if (docType === OCR_DOC_COUNTRY_TYPE.EXCLUDE) {
		return mapOcrCountryIds(countriesRaw);
	}
	return undefined;
}

/**
 * ¿Incluir un check de fraude OCR en el payload?
 * RECOMMENDED siempre; CUSTOM solo si el check está `available`.
 *
 * @param fraudTypeRaw - `fraud_scoring.type` (ISelect o enum)
 * @param checkAvailable - Flag `fraud_scoring.{check}.available`
 */
export function isOcrFraudCheckActiveForPayload(
	fraudTypeRaw: unknown,
	checkAvailable: boolean,
): boolean {
	const type = resolveOcrSelectOrPlain(fraudTypeRaw);
	return (
		type === DEMAND_LEVEL.RECOMMENDED ||
		(type === DEMAND_LEVEL.CUSTOM && checkAvailable)
	);
}

/**
 * ¿España (ISO `esp`) permitida para DNI según switches OCR del form?
 *
 * Abrir cuando: MiDNI / skip POL dependen de elegibilidad España.
 *
 * @param dniAvailable - Switch DNI
 * @param dniTypeRaw - ALL / INCLUDE / EXCLUDE (ISelect o enum)
 * @param countriesRaw - Lista de países del form
 */
export function isSpainAllowedForDniFromValues(
	dniAvailable: boolean,
	dniTypeRaw: unknown,
	countriesRaw: unknown,
): boolean {
	if (!dniAvailable) {
		return false;
	}
	const dniType = resolveOcrSelectOrPlain(dniTypeRaw);
	if (dniType === OCR_DOC_COUNTRY_TYPE.ALL) {
		return true;
	}
	const list = mapOcrCountryIds(countriesRaw).map((code) => code.toLowerCase());
	const hasSpain = list.some((code) => code === 'esp');
	if (dniType === OCR_DOC_COUNTRY_TYPE.INCLUDE) {
		return hasSpain;
	}
	if (dniType === OCR_DOC_COUNTRY_TYPE.EXCLUDE) {
		return !hasSpain;
	}
	return false;
}

/**
 * ¿Hay que forzar `skip_pol_when_midni_authenticated = false`?
 *
 * @param polAvailable - POL activa
 * @param midniEnabled - MiDNI activo
 * @param spainAllowed - {@link isSpainAllowedForDniFromValues}
 */
export function shouldClearSkipPolWhenMidni(
	polAvailable: boolean,
	midniEnabled: boolean,
	spainAllowed: boolean,
): boolean {
	return !polAvailable || !midniEnabled || !spainAllowed;
}

/** Valores de `OcrConfig.mode` / tipo de captura (NIF-802: + flexible). */
export type OcrCaptureMode = 'auto' | 'manual' | 'flexible';

/**
 * Clave i18n del label de tipo de captura OCR.
 *
 * @param mode - `auto` | `manual` | `flexible` (u otro → Activa por defecto)
 */
export function ocrCaptureModeLabelKey(mode: string | null | undefined): string {
	switch (mode) {
		case 'manual':
			return 'WORKFLOWS.FORM.OPERATIONS.OCR_MODE_STATIC';
		case 'flexible':
			return 'WORKFLOWS.FORM.OPERATIONS.OCR_MODE_FLEXIBLE';
		case 'auto':
		default:
			return 'WORKFLOWS.FORM.OPERATIONS.OCR_MODE_ACTIVE';
	}
}

/**
 * Clave i18n del método real usado en el intento OCR (`capture_method`).
 * Distinto de {@link ocrCaptureModeLabelKey} (config del workflow).
 *
 * @returns null si el valor no es un método conocido (el caller usa fallback a `mode`).
 */
export function ocrCaptureMethodLabelKey(captureMethod: string | null | undefined): string | null {
	if (!captureMethod || typeof captureMethod !== 'string') {
		return null;
	}
	switch (captureMethod.trim().toUpperCase()) {
		case 'UPLOAD':
			return 'PROCESSES.DETAIL.OCR_CAPTURE_METHOD.UPLOAD';
		case 'PHOTO':
			return 'PROCESSES.DETAIL.OCR_CAPTURE_METHOD.PHOTO';
		case 'SCAN':
			return 'PROCESSES.DETAIL.OCR_CAPTURE_METHOD.SCAN';
		case 'MIDNI':
			return 'PROCESSES.DETAIL.OCR_CAPTURE_METHOD.MIDNI';
		case 'EUDI':
			return 'PROCESSES.DETAIL.OCR_CAPTURE_METHOD.EUDI';
		default:
			return null;
	}
}

/**
 * Label bajo «Método captura» en detalle OCR: método real si existe; si no, modo del workflow.
 */
export function ocrDetailCaptureMethodLabelKey(
	captureMethod: string | null | undefined,
	mode: string | null | undefined,
): string {
	return ocrCaptureMethodLabelKey(captureMethod) ?? ocrCaptureModeLabelKey(mode);
}

/**
 * ¿El modo usa captura activa como método por defecto? (auto y flexible).
 * Usado para mostrar bloque de gestión de fallos del escaneo automático.
 */
export function isOcrActiveCaptureDefault(mode: string | null | undefined): boolean {
	return mode === 'auto' || mode === 'flexible';
}
